import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Eye, CheckCircle2, ShieldCheck, ArrowRight, PencilLine } from 'lucide-react';
import { directoryApi } from '../lib/api';
import { DfwGymCard } from '../components/Articles/dfw/DfwGymCard';

const noop = () => {};

function ApprovalBox({ gymId, gymName, approval, editRequest, onApproved, onEditRequested }) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [changes, setChanges] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (approval?.approved) {
    return (
      <div className="border border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.06] p-6 flex items-start gap-3" data-testid="gym-preview-approved-banner">
        <CheckCircle2 className="w-6 h-6 text-[#C7FA3E] flex-shrink-0" />
        <div>
          <p className="text-white text-sm font-semibold">Listing approved by {approval.name}</p>
          <p className="text-zinc-400 text-xs mt-1">
            {new Date(approval.approved_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} — thanks for the sign-off! Your listing is cleared to go live on the directory.
          </p>
          {approval.notes && <p className="text-zinc-500 text-xs mt-2 italic">"{approval.notes}"</p>}
        </div>
      </div>
    );
  }

  const approve = async () => {
    if (!name.trim()) { setError('Please enter your name'); return; }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await directoryApi.approveGymListing(gymId, { name, notes });
      onApproved(data.approval);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  const requestChanges = async () => {
    if (!name.trim()) { setError('Please enter your name'); return; }
    if (!changes.trim()) { setError("Please describe the changes you'd like"); return; }
    setSubmitting(true);
    setError('');
    try {
      const { data } = await directoryApi.requestListingChanges(gymId, { name, changes });
      onEditRequested(data.request);
      setShowEdit(false);
      setChanges('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {editRequest && (
        <div className="border border-amber-500/40 bg-amber-500/[0.06] p-5 flex items-start gap-3" data-testid="gym-preview-edit-request-banner">
          <PencilLine className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white text-sm font-semibold">Change request sent{editRequest.name ? ` by ${editRequest.name}` : ''}</p>
            <p className="text-zinc-400 text-xs mt-1">
              {new Date(editRequest.requested_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} — we're on it. Once your listing is updated, come back to this same link to approve.
            </p>
            <p className="text-zinc-500 text-xs mt-2 italic whitespace-pre-wrap">"{editRequest.changes}"</p>
          </div>
        </div>
      )}
      <div className="border border-zinc-800 bg-zinc-900/50 p-6" data-testid="gym-preview-approval-form">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-[#C7FA3E]" />
          <h2 className="text-white text-lg" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.03em' }}>Approve This Listing</h2>
        </div>
        <p className="text-zinc-400 text-sm mb-4">
          Everything look right for {gymName}? Tap approve and we'll publish it exactly as shown — or request specific changes and we'll update it before it goes live.
        </p>
        <div className="space-y-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name *"
            className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 text-sm focus:border-[#C7FA3E] focus:outline-none"
            data-testid="gym-preview-approve-name"
          />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note with your approval"
            className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 text-sm focus:border-[#C7FA3E] focus:outline-none min-h-[70px] resize-none"
            data-testid="gym-preview-approve-notes"
          />
        </div>
        {error && <p className="text-red-400 text-xs mt-2" data-testid="gym-preview-approve-error">{error}</p>}
        <div className="flex flex-wrap items-center gap-4 mt-4">
          <button
            type="button"
            onClick={approve}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#C7FA3E] hover:bg-[#b3e62e] text-black text-sm font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="gym-preview-approve-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Approve Listing
          </button>
          <button
            type="button"
            onClick={() => setShowEdit((v) => !v)}
            className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-amber-400 text-xs uppercase tracking-wider transition-colors"
            data-testid="gym-preview-toggle-edit-request"
          >
            <PencilLine className="w-3.5 h-3.5" /> {showEdit ? 'Never mind' : 'Request changes instead'}
          </button>
        </div>
        {showEdit && (
          <div className="mt-4 pt-4 border-t border-zinc-800" data-testid="gym-preview-edit-request-section">
            <p className="text-zinc-400 text-xs mb-2">Tell us exactly what to change — pricing, schedule, wording, links, photos…</p>
            <textarea
              value={changes}
              onChange={(e) => setChanges(e.target.value)}
              placeholder='e.g. "Drop-in price is now $22, and please list our 6am Tuesday class"'
              className="w-full bg-zinc-900 border border-zinc-700 text-white px-4 py-2.5 text-sm focus:border-amber-400 focus:outline-none min-h-[90px] resize-none"
              data-testid="gym-preview-changes-text"
            />
            <button
              type="button"
              onClick={requestChanges}
              disabled={submitting}
              className="mt-3 inline-flex items-center gap-2 px-6 py-2.5 border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="gym-preview-request-changes-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PencilLine className="w-4 h-4" />}
              Send Change Request
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function GymPreviewPage() {
  const { gymId } = useParams();
  const [data, setData] = useState(null);
  const [approval, setApproval] = useState(null);
  const [editRequest, setEditRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    directoryApi.getGymPreview(gymId)
      .then(({ data: d }) => { setData(d); setApproval(d.owner_approval); setEditRequest(d.owner_edit_request); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [gymId]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
      </main>
    );
  }

  if (notFound || !data?.gym) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing text-center" data-testid="gym-preview-not-found">
          <h1 className="text-white text-4xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Listing Not Found</h1>
          <p className="text-zinc-400 text-sm">This preview link may be outdated — reach out to the NinjAthlete team for a fresh one.</p>
        </div>
      </main>
    );
  }

  const { gym, city_label: cityLabel, article_slug: articleSlug } = data;

  return (
    <main className="pt-20 min-h-screen" data-testid="gym-preview-page">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <p className="flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          <Eye className="w-3.5 h-3.5" /> Listing Preview · {cityLabel} HYROX Directory
        </p>
        <h1 className="text-white text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }}>
          {gym.name}
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          This is exactly how <strong className="text-zinc-200">{gym.name}</strong> will appear in NinjAthlete's{' '}
          <strong className="text-zinc-200">Best HYROX Training Gyms in {cityLabel}</strong> directory. All links are live so you can
          test them — nothing is published to the public directory page until you approve below.
        </p>

        <div className="mb-8" data-testid="gym-preview-card-wrap">
          <DfwGymCard gym={gym} onAddVideo={noop} onClaim={noop} />
        </div>

        <ApprovalBox gymId={gymId} gymName={gym.name} approval={approval} editRequest={editRequest} onApproved={setApproval} onEditRequested={setEditRequest} />

        {approval?.approved && articleSlug && (
          <Link
            to={`/blogs/article/${articleSlug}`}
            className="inline-flex items-center gap-1.5 mt-6 text-[#C7FA3E] text-xs uppercase tracking-widest hover:gap-2.5 transition-all"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="gym-preview-view-directory-link"
          >
            View the live {cityLabel} directory <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </main>
  );
}
